/* Bumblebee primitives.

   Styling lives in primitives.module.css. Hover, focus and responsive
   behaviour are plain CSS — this is served as a website, not embedded in
   Canvas, so there is no reason to simulate :hover in JavaScript.
*/

import { useMemo } from 'react';
import s from './primitives.module.css';

const cx = (...v) => v.filter(Boolean).join(' ');

/* ---------- text ---------- */
/** `spacing`: tight | wide | wider · `tone`: lemon | onDark */
export const Kicker = ({ children, spacing, tone, className, style }) => (
  <span className={cx(s.kicker, spacing && s[spacing], tone && s[tone], className)} style={style}>
    {children}
  </span>
);

export const Meta = ({ children, className, style }) => (
  <span className={cx(s.meta, className)} style={style}>{children}</span>
);

/** kicker · hairline · right-aligned meta — the header rule on readers and quizzes */
export const RuleRow = ({ kicker, meta, className }) => (
  <div className={cx(s.ruleRow, className)}>
    <Kicker>{kicker}</Kicker>
    <span className={s.line} />
    {meta != null && <Meta>{meta}</Meta>}
  </div>
);

export const Prose = ({ children, className, style }) => (
  <p className={cx(s.prose, className)} style={style}>{children}</p>
);

export const InlineCode = ({ children, className }) => (
  <code className={cx(s.inlineCode, className)}>{children}</code>
);

/* ---------- syntax highlighting ---------- */
/* JavaScript's keywords as lessons use them, plus the few other-track words that
   cannot collide with a JavaScript name. Python's `and`, `or`, `not` and `is` are
   left out on purpose: `not` is a function students write in primitives-game. */
const KEYWORDS = ['function','let','const','var','return','if','else','for','while','do',
  'switch','case','break','continue','default','class','extends','super','this','static',
  'new','delete','void','yield','debugger','def','elif','end','import','from','export',
  'typeof','instanceof','async','await','true','false','null','nil','None','undefined',
  'of','in','try','catch','finally','throw'];

const TOKEN = new RegExp(
  '(`(?:[^`\\\\]|\\\\.)*`|"(?:[^"\\\\]|\\\\.)*"|\'(?:[^\'\\\\]|\\\\.)*\')' +   // strings
  `|\\b(${KEYWORDS.join('|')})\\b` +                                            // keywords
  '|\\b(\\d+\\.?\\d*)\\b',                                                      // numbers
  'g');

/** Split one line of source into classed segments. Comments win over everything. */
function highlightLine(line) {
  const ci = line.search(/\/\/|#(?!\{)/);
  const head = ci >= 0 ? line.slice(0, ci) : line;
  const tail = ci >= 0 ? line.slice(ci) : '';
  const out = [];
  let last = 0, m;
  TOKEN.lastIndex = 0;
  while ((m = TOKEN.exec(head)) !== null) {
    if (m.index > last) out.push({ t: head.slice(last, m.index) });
    if (m[1]) out.push({ t: m[1], cls: s.str });
    else if (m[2]) out.push({ t: m[2], cls: s.kw });
    else out.push({ t: m[3], cls: s.num });
    last = m.index + m[0].length;
  }
  if (last < head.length) out.push({ t: head.slice(last) });
  if (tail) out.push({ t: tail, cls: s.com });
  return out;
}

export function Code({ children }) {
  const lines = useMemo(() => String(children).replace(/\n$/, '').split('\n'), [children]);
  return lines.map((line, i) => (
    <span key={i}>
      {highlightLine(line).map((seg, j) => <span key={j} className={seg.cls}>{seg.t}</span>)}
      {i < lines.length - 1 ? '\n' : ''}
    </span>
  ));
}

/** `numbered`: line numbers in a gutter, as an editor shows them. They are CSS
    counters, so copying the code does not copy the numbers. */
export function CodeBlock({ children, filename, numbered, className }) {
  return (
    <div className={cx(s.codeBlock, className)}>
      {filename && <div className={s.codeBar}>{filename}</div>}
      <pre className={cx(s.pre, numbered && s.numbered)}><Code>{children}</Code></pre>
    </div>
  );
}

/* ---------- directive blocks ---------- */
const BLOCK_NAME = {
  notice: 'NOTICE', check: 'CHECKPOINT', try: 'TRY IT', watch: 'WATCH',
  track: 'YOUR TRACK', lookup: 'REFERENCE', submit: 'SUBMIT',
};
const BORDERED = new Set(['check', 'try', 'track']);

export function Block({ kind = 'notice', label, children, className }) {
  const k = BLOCK_NAME[kind] ? kind : 'notice';
  return (
    <div className={cx(s.block, s[k], BORDERED.has(k) && s.borderLeft, className)}>
      <div className={s.blockLabel}>{BLOCK_NAME[k]}{label ? ` · ${label}` : ''}</div>
      <div className={s.blockBody}>{children}</div>
    </div>
  );
}

/* `entries` — a glossary, one paragraph per term, so each can be scanned
   and re-read on its own. */
export const MarginNote = ({ label = 'NOTE', children, boxed, entries }) => (
  <div className={cx(s.marginNote, boxed && s.boxed)}>
    <Kicker>{label}</Kicker>
    {entries ? entries.map((e, i) => <p key={i} className={s.entry}>{e}</p>) : <p>{children}</p>}
  </div>
);

/* ---------- controls ---------- */
export function Button({ children, variant = 'primary', full, className, ...rest }) {
  return (
    <button type="button" className={cx(s.btn, s[variant], full && s.full, className)} {...rest}>
      {children}
    </button>
  );
}

export const Meter = ({ pct, className, style }) => (
  <div className={cx(s.meter, className)} style={style}
       role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
    <span style={{ width: `${pct}%` }} />
  </div>
);

export const Stat = ({ label, children }) => (
  <div className={s.stat}><dt>{label}</dt><dd>{children}</dd></div>
);
